import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ContextType } from 'src/interface';
import { HOTEL_CUSTOMER_SESSION } from 'src/constants';
import {
  checkIsLaptop,
  getLocalStorageItem,
  setLocalStorageItem,
} from 'src/helper';

export const Context = createContext<ContextType>({
  header: {
    width: document.body.offsetWidth,
    height: 52,
    setSize: () => {},
  },
  screen: {
    isLaptop: checkIsLaptop(),
    setIsLaptop: () => {},
  },
  layoutScroller: {
    trigger: { x: 0, y: 0 },
    scroll: () => {},
  },
  escHandler: {
    closeModalId: undefined,
    addToModalList: () => {},
    removeFromModalList: () => {},
    clearCloseModalId: () => {},
  },
  navigator: {
    backUrl: null,
    setBackUrl: () => {},
  },
  hcs: {
    hcsIsVisible: getLocalStorageItem(HOTEL_CUSTOMER_SESSION, false),
    toggleHcsIsVisible: () => {},
  },
});

export const useContextData = () => useContext(Context);

export const useContextHandler = (): ContextType => {
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const [headerSize, setHeaderSize] = useState({
    height: 52,
    width: document.body.offsetWidth,
  });
  const [isLaptop, setIsLaptop] = useState(checkIsLaptop());
  const [scrollTrigger, setScrollTrigger] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [hcsIsVisible, setHcsIsVisible] = useState(
    getLocalStorageItem(HOTEL_CUSTOMER_SESSION, false),
  );

  const toggleHcsIsVisible = useCallback(() => {
    setHcsIsVisible((prev) => {
      setLocalStorageItem(HOTEL_CUSTOMER_SESSION, !prev);
      return !prev;
    });
  }, []);

  const [closeModalId, setCloseModalId] = useState<string | undefined>(
    undefined,
  );
  const [, setModalList] = useState<string[]>([]);
  const addToModalList = useCallback((id: string) => {
    setModalList((list) => [...list, id]);
  }, []);
  const removeFromModalList = useCallback((id: string) => {
    setModalList((list) => {
      const index = list.findIndex((item) => item === id);

      if (index === -1) {
        return list;
      }

      const updatedList = [...list];
      updatedList.splice(index, 1);
      return updatedList;
    });
  }, []);
  const clearCloseModalId = useCallback(() => {
    setCloseModalId(undefined);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setModalList((list) => {
          setCloseModalId(list.slice(-1)[0]);
          return list;
        });
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return {
    header: {
      width: headerSize.width,
      height: headerSize.height,
      setSize: setHeaderSize,
    },
    screen: {
      isLaptop,
      setIsLaptop,
    },
    layoutScroller: {
      trigger: scrollTrigger,
      scroll: useCallback((x = 0, y = 0) => {
        setScrollTrigger({ x, y });
      }, []),
    },
    escHandler: {
      closeModalId,
      addToModalList,
      removeFromModalList,
      clearCloseModalId,
    },
    navigator: {
      backUrl,
      setBackUrl,
    },
    hcs: {
      hcsIsVisible,
      toggleHcsIsVisible,
    },
  };
};
